import { BaseController } from '../../base_controller';

const UNIQUE_ID = 'ballots--choice-group-instructions-component';
const MOBILE_BREAKPOINT = 480;

interface ChoiceGroup {
  id: number;
  name: string;
  min_required: number;
  max_allowed: number | null;
  display_on_ballot: boolean;
  answer_ids: number[];
}

interface AnswerGroupMap {
  [answerId: string]: number[];
}

export default class extends BaseController {
  static targets = ['groupItem', 'progressBar', 'countText', 'statusIcon', 'collapseToggle', 'collapseIcon', 'groupsList'];
  static values = {
    groups: String,
    answerGroupMap: String,
    scoredAnswerIds: String,
  };

  declare groupItemTargets: HTMLElement[];
  declare progressBarTargets: HTMLElement[];
  declare countTextTargets: HTMLElement[];
  declare statusIconTargets: HTMLElement[];
  declare collapseToggleTarget: HTMLElement;
  declare collapseIconTarget: HTMLElement;
  declare groupsListTarget: HTMLElement;
  declare hasCollapseToggleTarget: boolean;
  declare hasGroupsListTarget: boolean;
  declare groupsValue: string;
  declare answerGroupMapValue: string;
  declare scoredAnswerIdsValue: string;

  private groups: ChoiceGroup[] = [];
  private answerGroupMap: AnswerGroupMap = {};
  // Server-authored set of answer ids belonging to scored questions -- the only discriminator
  // for a scored <select> vs. a preferential one, since their DOM shapes are identical.
  private scoredAnswerIds: Set<number> = new Set();
  private selectionCounts: { [groupId: number]: number } = {};
  private formElement: HTMLFormElement | null = null;
  private boundChangeHandler: ((event: Event) => void) | null = null;
  private boundSlateHandler: (() => void) | null = null;
  private boundResizeHandler: (() => void) | null = null;
  private isCollapsed = false;
  private slateQuestionIds: Set<string> | null = null;

  connect(): void {
    this.parseData();
    this.attachFormListener();
    this.attachResizeListener();
    // Compute existing selections so progress bars reflect pre-checked inputs on load
    this.calculateSelectionCounts();
    this.updateAllGroups();
    this.checkAbstainVisibility();
  }

  disconnect(): void {
    this.detachFormListener();
    this.detachResizeListener();
    this.slateQuestionIds = null;
  }

  private parseData(): void {
    try {
      this.groups = JSON.parse(this.groupsValue);
      this.answerGroupMap = JSON.parse(this.answerGroupMapValue);
      this.scoredAnswerIds = new Set(JSON.parse(this.scoredAnswerIdsValue));
    } catch (error) {
      console.error('Failed to parse choice group data:', error);
      this.groups = [];
      this.answerGroupMap = {};
      this.scoredAnswerIds = new Set();
    }
  }

  private attachFormListener(): void {
    // Listen for change events on the entire ballot form
    const form = this.element.closest('form') as HTMLFormElement | null;
    if (!form) {
      console.warn('No parent form found for choice group instructions');
      return;
    }

    this.formElement = form;

    // Handle individual checkbox/radio/select changes (manual user selections); any select
    // change is a valid recount trigger, since discriminating scored happens in calculateSelectionCounts.
    this.boundChangeHandler = (event: Event) => {
      const target = event.target as HTMLInputElement | HTMLSelectElement;

      if (
        (target.tagName === 'INPUT' &&
          (target.type === 'checkbox' || target.type === 'radio')) ||
        target.tagName === 'SELECT'
      ) {
        this.handleSelectionChange();
      }
    };

    // Handle bulk slate selections (custom event from slates controller)
    this.boundSlateHandler = () => {
      this.handleSelectionChange();
    };

    form.addEventListener('change', this.boundChangeHandler);
    form.addEventListener('slate:selections-applied', this.boundSlateHandler);
  }

  private detachFormListener(): void {
    if (!this.formElement) return;

    if (this.boundChangeHandler) {
      this.formElement.removeEventListener('change', this.boundChangeHandler);
    }
    if (this.boundSlateHandler) {
      this.formElement.removeEventListener('slate:selections-applied', this.boundSlateHandler);
    }

    this.formElement = null;
    this.boundChangeHandler = null;
    this.boundSlateHandler = null;
  }

  private handleSelectionChange(): void {
    this.calculateSelectionCounts();
    this.updateAllGroups();
    this.checkAbstainVisibility();
  }

  private calculateSelectionCounts(): void {
    // Reset counts
    this.selectionCounts = {};
    this.groups.forEach((group) => {
      this.selectionCounts[group.id] = 0;
    });

    // Find the parent form
    const form = this.element.closest('form');
    if (!form) return;

    // `select:not(:disabled)` is the select-side equivalent of `:checked`: the abstain handler
    // only disables a question's selects (leaving a stale value), rather than unchecking them.
    const checkedInputs = form.querySelectorAll<HTMLInputElement | HTMLSelectElement>(
      'input[type="checkbox"]:checked, input[type="radio"]:checked, select:not(:disabled)'
    );

    checkedInputs.forEach((input) => {
      const answerId = this.extractAnswerId(input);
      if (!answerId) return;

      if (input.tagName === 'SELECT') {
        // No DOM-shape sniffing -- the server-authored scored answer id set is the only
        // discriminator, since selects are otherwise identical to preferential ones.
        if (!this.scoredAnswerIds.has(answerId)) return;

        // A select counts when its value is a non-empty string, never by magnitude --
        // '0' is a legal score and counts, as does 'not_applicable'.
        if (input.value === '') return;
      }

      // Find which groups this answer belongs to
      const groupIds = this.answerGroupMap[answerId] || [];
      groupIds.forEach((groupId) => {
        this.selectionCounts[groupId] = (this.selectionCounts[groupId] || 0) + 1;
      });
    });
  }

  private extractAnswerId(input: HTMLInputElement | HTMLSelectElement): number | null {
    // Try to extract answer ID from input value (for radio buttons)
    if (input.type === 'radio' && input.value) {
      const id = parseInt(input.value, 10);
      if (!isNaN(id)) return id;
    }

    // Checkbox/select ids follow Rails' ballot_data_questions_{q_id}_answers_{a_id} generation.
    const match = input.id.match(/answers_(\d+)/);
    if (match) {
      return parseInt(match[1], 10);
    }

    return null;
  }

  private updateAllGroups(): void {
    this.groups.forEach((group) => {
      this.updateGroupUI(group);
    });
  }

  private updateGroupUI(group: ChoiceGroup): void {
    const count = this.selectionCounts[group.id] || 0;
    const min = group.min_required;
    const max = group.max_allowed || min;

    // Determine status
    let status: 'valid' | 'invalid' | 'neutral' = 'neutral';
    if (count < min) {
      status = 'invalid';
    } else if (max && count > max) {
      status = 'invalid';
    } else if (count >= min) {
      status = 'valid';
    }

    // Update progress bar
    this.updateProgressBar(group.id, count, max, status);

    // Update count text
    this.updateCountText(group.id, count, max);

    // Update status icon
    this.updateStatusIcon(group.id, status);

    // Update group container styling
    this.updateGroupContainer(group.id, status);
  }

  private updateProgressBar(
    groupId: number,
    count: number,
    max: number,
    status: string
  ): void {
    const progressBar = this.findTargetByGroupId(
      this.progressBarTargets,
      groupId
    );
    if (!progressBar) return;

    const percentage = max > 0 ? Math.min((count / max) * 100, 100) : 0;
    progressBar.style.width = `${percentage}%`;
    progressBar.setAttribute('aria-valuenow', count.toString());

    // Update status class
    progressBar.classList.remove('status-valid', 'status-invalid');
    if (status !== 'neutral') {
      progressBar.classList.add(`status-${status}`);
    }
  }

  private updateCountText(groupId: number, count: number, max: number): void {
    const countText = this.findTargetByGroupId(this.countTextTargets, groupId);
    if (!countText) return;

    countText.textContent = `${count} / ${max}`;
  }

  private updateStatusIcon(groupId: number, status: string): void {
    const statusIcon = this.findTargetByGroupId(
      this.statusIconTargets,
      groupId
    );
    if (!statusIcon) return;

    // Swap Font Awesome class based on status
    const iconEl = statusIcon.querySelector('i');
    if (iconEl) {
      iconEl.className = ''; // reset
      if (status === 'valid') {
        iconEl.className = 'fas fa-check-circle';
      } else if (status === 'invalid') {
        iconEl.className = 'fas fa-times-circle';
      } else {
        iconEl.className = 'fas fa-circle';
      }
    }

    statusIcon.classList.remove('status-valid', 'status-invalid');
    if (status !== 'neutral') {
      statusIcon.classList.add(`status-${status}`);
    }
  }

  private updateGroupContainer(groupId: number, status: string): void {
    const groupContainer = this.findTargetByGroupId(
      this.groupItemTargets,
      groupId
    );
    if (!groupContainer) return;

    groupContainer.classList.remove('status-valid', 'status-invalid');
    if (status !== 'neutral') {
      groupContainer.classList.add(`status-${status}`);
    }
  }

  private findTargetByGroupId(
    targets: HTMLElement[],
    groupId: number
  ): HTMLElement | undefined {
    return targets.find(
      (target) => target.dataset.groupId === groupId.toString()
    );
  }

  private getSlateQuestionIds(): Set<string> {
    if (this.slateQuestionIds !== null) return this.slateQuestionIds;

    const ids = new Set<string>();
    Object.keys(this.answerGroupMap).forEach((answerId) => {
      // Try checkbox input or scored <select> first (id contains answers_{answerId}) --
      // needed so the full-abstention hide path also works for scored-only slates.
      let input = document.querySelector<HTMLInputElement | HTMLSelectElement>(
        `input[id*="answers_${answerId}"], select[id*="answers_${answerId}"]`
      );
      // Fallback to radio input matched by value
      if (!input) {
        input = document.querySelector<HTMLInputElement>(
          `input[value="${answerId}"]`
        );
      }
      if (input) {
        const questionEl = input.closest('[id^="question_"]');
        if (questionEl) {
          const match = questionEl.id.match(/^question_(\d+)$/);
          if (match) ids.add(match[1]);
        }
      }
    });

    this.slateQuestionIds = ids;
    return ids;
  }

  private checkAbstainVisibility(): void {
    const questionIds = this.getSlateQuestionIds();
    if (questionIds.size === 0) return;

    const allAbstained = Array.from(questionIds).every((qId) =>
      document.querySelector(
        `#ballot_data_questions_${qId}_answers_abstain:checked,` +
        `#ballot_data_questions_${qId}_answer_abstain:checked`
      ) !== null
    );

    this.element.hidden = allAbstained;
  }

  // --- Collapse / Expand (mobile only) ---

  toggleCollapse(): void {
    if (!this.hasGroupsListTarget) return;

    this.isCollapsed = !this.isCollapsed;

    const groupsList = this.groupsListTarget;
    const collapseModifier = `${UNIQUE_ID}__groups--collapsed`;
    const iconModifier = `${UNIQUE_ID}__collapse-icon--collapsed`;

    if (this.isCollapsed) {
      groupsList.classList.add(collapseModifier);
      if (this.hasCollapseToggleTarget) {
        this.collapseToggleTarget.setAttribute('aria-expanded', 'false');
      }
      this.collapseIconTarget?.classList.add(iconModifier);
    } else {
      groupsList.classList.remove(collapseModifier);
      if (this.hasCollapseToggleTarget) {
        this.collapseToggleTarget.setAttribute('aria-expanded', 'true');
      }
      this.collapseIconTarget?.classList.remove(iconModifier);
    }
  }

  private attachResizeListener(): void {
    this.boundResizeHandler = () => this.handleResize();
    window.addEventListener('resize', this.boundResizeHandler);
    // Ensure correct initial state
    this.handleResize();
  }

  private detachResizeListener(): void {
    if (this.boundResizeHandler) {
      window.removeEventListener('resize', this.boundResizeHandler);
      this.boundResizeHandler = null;
    }
  }

  private handleResize(): void {
    if (!this.hasGroupsListTarget) return;

    const isMobile = window.innerWidth <= MOBILE_BREAKPOINT;
    const collapseModifier = `${UNIQUE_ID}__groups--collapsed`;

    // When switching from mobile to desktop, ensure groups are expanded
    if (!isMobile && this.isCollapsed) {
      this.isCollapsed = false;
      this.groupsListTarget.classList.remove(collapseModifier);
      if (this.hasCollapseToggleTarget) {
        this.collapseToggleTarget.setAttribute('aria-expanded', 'true');
      }
      const iconModifier = `${UNIQUE_ID}__collapse-icon--collapsed`;
      this.collapseIconTarget?.classList.remove(iconModifier);
    }
  }
}
